import lodash from 'lodash';
import React from 'react';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useGetDecisionTreeRunResultQuery } from '../../../../generated/core-api/graphql-react-query';
import useDecisionTreeRunContext from '../DecisionTreeRunContext/useDecisionTreeRunContext';
import { DecisionTreeResultContext } from './useDecisionTreeResultContext';

export default function DecisionTreeResultContextProvider({ children }: { children: React.ReactNode }) {
  const [showResult, setShowResult] = useQueryParam('show-dt-result', BooleanParam);

  const { run, runId } = useDecisionTreeRunContext();
  const runRef = React.useRef(run);

  const setExpanded = React.useCallback((newValue: boolean) => {
    if (newValue) {
      setShowResult(true);
    } else {
      setShowResult(undefined);
    }
  }, [setShowResult]);

  const runIdResult: string | null = React.useMemo(() => {
    if (!runId) return null;
    if (!run) return null;
    if (!run.finishedAt) return null;
    if (run.parentId) return null;
    return runId;
  }, [runId, run]);

  const result = useGetDecisionTreeRunResultQuery({ input: { id: runIdResult ?? '' } }, { enabled: !!runIdResult && runIdResult !== '' });
  React.useEffect(() => {
    if ((run && !run.finishedAt) || !runId) {
      setExpanded(false);
    } else if (run && !run.parentId && run.finishedAt && !run.sequence?.length) {
      setExpanded(true);
    }
  }, [run]);

  React.useEffect(() => {
    if (showResult && run && runRef.current) {
      const fullHistory = {
        main: run.history?.map((item) => item.nodeId),
        sequence: run.sequence?.map((sequenceItem) => ({
          id: sequenceItem.runId,
          history: sequenceItem.run?.history.map((historyItem) => historyItem.nodeId),
        })),
      };
      const previousHistory = {
        main: runRef.current.history?.map((item) => item.nodeId),
        sequence: runRef.current.sequence?.map((sequenceItem) => ({
          id: sequenceItem.runId,
          history: sequenceItem.run?.history.map((historyItem) => historyItem.nodeId),
        })),
      };
      if (!lodash.isEqual(fullHistory, previousHistory)) {
        result.refetch();
      }
    }
    runRef.current = run;
  }, [showResult, run, runRef]);

  const value = React.useMemo(() => ({
    expanded: !!showResult,
    setExpanded,
    result: result.data?.getDecisionTreeRun,
    generateReport() { },
  }), [showResult, result.data?.getDecisionTreeRun]);

  return (
    <DecisionTreeResultContext.Provider value={value}>
      {children}
    </DecisionTreeResultContext.Provider>
  );
}
